/**
 * 计费系数可视化编辑器
 *
 * 把 { "5": 1.0, "10": 2.0 } 渲染成行编辑表格，运营改起来不用直接编辑 JSON。
 */

import { useState, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';

interface MultiplierEditorProps {
  label: string;
  hint?: string;
  /** 行 key 类型：number / string，仅影响输入框 type */
  keyType?: 'number' | 'string';
  /** 系数键名输入框的 placeholder */
  keyPlaceholder?: string;
  /** 系数值输入框的 placeholder */
  valuePlaceholder?: string;
  /** 当前值（已解析的对象） */
  value: Record<string, number>;
  onChange: (value: Record<string, number>) => void;
  /** 固定的 key 列表（不可增删改 key，只能改 value） */
  fixedKeys?: string[];
  /** 固定 key 的显示名称映射 */
  keyLabels?: Record<string, string>;
}

interface Row {
  key: string;
  value: string;
}

function valueToRows(value: Record<string, number>): Row[] {
  return Object.entries(value).map(([k, v]) => ({
    key: k,
    value: String(v),
  }));
}

function rowsToValue(rows: Row[]): Record<string, number> {
  const obj: Record<string, number> = {};
  for (const r of rows) {
    const k = r.key.trim();
    if (!k) continue;
    const v = Number(r.value);
    if (!Number.isFinite(v)) continue;
    obj[k] = v;
  }
  return obj;
}

export function MultiplierEditor({
  label,
  hint,
  keyType = 'string',
  keyPlaceholder = '键',
  valuePlaceholder = '系数',
  value,
  onChange,
  fixedKeys,
  keyLabels,
}: MultiplierEditorProps) {
  // fixedKeys 模式：不用行状态，直接渲染固定行
  if (fixedKeys && fixedKeys.length > 0) {
    const setFixedValue = (key: string, v: string) => {
      const num = Number(v);
      onChange({ ...value, [key]: Number.isFinite(num) ? num : 1 });
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-light-text">{label}</label>
        </div>
        {hint && <p className="text-xs text-light-muted mb-1.5">{hint}</p>}
        <div className="space-y-1">
          {fixedKeys.map((key) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-sm text-light-secondary w-20 shrink-0">
                {keyLabels?.[key] || key}
              </span>
              <span className="text-light-muted text-xs">×</span>
              <input
                type="number"
                step={0.1}
                value={value[key] ?? 1}
                onChange={(e) => setFixedValue(key, e.target.value)}
                className="input-field w-20 text-sm py-1.5 px-2"
                placeholder={valuePlaceholder}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 普通模式（可增删行）
  // 内部维护行状态（允许空行存在），外部 onChange 只提交有效数据
  const [rows, setRows] = useState<Row[]>(() => valueToRows(value));
  const internalChange = useRef(false);

  // 外部 value 变化时同步（但忽略自身触发的变化）
  useEffect(() => {
    if (internalChange.current) {
      internalChange.current = false;
      return;
    }
    setRows(valueToRows(value));
  }, [value]);

  const commitRows = (next: Row[]) => {
    setRows(next);
    internalChange.current = true;
    onChange(rowsToValue(next));
  };

  const addRow = () => {
    setRows((prev) => [...prev, { key: '', value: '1' }]);
  };

  const removeRow = (idx: number) => {
    const next = rows.filter((_, i) => i !== idx);
    commitRows(next);
  };

  const setRowKey = (idx: number, k: string) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, key: k } : r));
    commitRows(next);
  };

  const setRowValue = (idx: number, v: string) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, value: v } : r));
    commitRows(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-light-text">{label}</label>
      </div>
      {hint && <p className="text-xs text-light-muted mb-2">{hint}</p>}
      <div className="space-y-1.5">
        {rows.map((r, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              type={keyType === 'number' ? 'number' : 'text'}
              value={r.key}
              onChange={(e) => setRowKey(idx, e.target.value)}
              className="input-field flex-1 text-sm"
              placeholder={keyPlaceholder}
            />
            <span className="text-light-muted">×</span>
            <input
              type="number"
              step={0.1}
              value={r.value}
              onChange={(e) => setRowValue(idx, e.target.value)}
              className="input-field w-24 text-sm"
              placeholder={valuePlaceholder}
            />
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"
              title="删除"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1 text-xs text-accent-blue hover:underline"
        >
          <Plus className="w-3.5 h-3.5" />
          添加一行
        </button>
      </div>
    </div>
  );
}

/**
 * 数组编辑器（用于 supportedDurations / supportedResolutions / supportedAspectRatios）
 */
interface StringListEditorProps {
  label: string;
  hint?: string;
  itemType?: 'number' | 'string';
  placeholder?: string;
  value: Array<string | number>;
  onChange: (value: Array<string | number>) => void;
}

export function StringListEditor({
  label,
  hint,
  itemType = 'string',
  placeholder,
  value,
  onChange,
}: StringListEditorProps) {
  const handleSet = (idx: number, v: string) => {
    const next = value.map((item, i) => {
      if (i !== idx) return item;
      return itemType === 'number' ? Number(v) : v;
    });
    onChange(next);
  };
  const handleAdd = () => {
    onChange([...value, itemType === 'number' ? 0 : '']);
  };
  const handleRemove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label className="text-sm font-medium text-light-text block mb-1.5">{label}</label>
      {hint && <p className="text-xs text-light-muted mb-2">{hint}</p>}
      <div className="flex flex-wrap gap-1.5">
        {value.map((v, idx) => (
          <div key={idx} className="flex items-center gap-1 bg-light-hover rounded-lg pl-2">
            <input
              type={itemType === 'number' ? 'number' : 'text'}
              value={String(v)}
              onChange={(e) => handleSet(idx, e.target.value)}
              className="bg-transparent w-16 py-1 text-sm focus:outline-none"
              placeholder={placeholder}
            />
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              className="p-1 rounded-lg text-rose-500 hover:bg-rose-50"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed border-light-border text-xs text-light-muted hover:border-accent-blue hover:text-accent-blue"
        >
          <Plus className="w-3 h-3" />
          添加
        </button>
      </div>
    </div>
  );
}
